import 'react-native-gesture-handler';
import React, { useEffect, useState } from 'react';
import { LogBox, Platform, StatusBar, useWindowDimensions, View } from 'react-native';
import { NavigationContainer } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { Provider } from 'react-redux';
import Toast from 'react-native-toast-message';
import BootSplash from 'react-native-bootsplash';

import DrawerStack from './src/stacks/drawerStack';
import AuthStack from './src/stacks/authStack';
//redux
import { store } from './src/redux/store';
import { useAppSelector, useAppDispatch } from './src/redux/hooks';
import { setBarcodeValue, setDeviceMode, setLandscape } from './src/redux/slices/commonSlice';

import { SafeAreaProvider, SafeAreaView } from 'react-native-safe-area-context';
import { ThemeWrapper } from './src/themes/themeWrapper';
import BarcodeListener from './src/components/newSellscreen/barcodeListerner';
import { documentTitle, linking } from './src/platform/navigation/linking';
import { AppRoot } from './src/platform/root/appRoot';
import { AuthSplash, resolveInitialAuth } from './src/platform/auth/authBoot';
import { ZenToastConfig } from 'zen-core-elements';


function App(): React.JSX.Element {
  LogBox.ignoreLogs([
    "react-native-gesture-handler] Seems like you're using an old API with gesture components, check out new Gestures system!",
    "Toolbar has no editor. Please make sure the prop getEditor returns a ref to the editor component.",
    "InteractionManager has been deprecated and will be removed in a future release."
  ])

  return (
    <Provider store={store}>
      <AppRoot>
        <SafeAreaView style={{ flex: 1 }} edges={["bottom"]} >
          <ThemeWrapper>
            <AppIndex />
          </ThemeWrapper>
        </SafeAreaView>
      </AppRoot>
    </Provider>
  );
}

export const AppIndex = () => {
  const screenMode = useAppSelector((state) => state?.display?.screenMode)
  const isLoggedIn = useAppSelector((state) => state?.common?.isLoggedIn)
  const [loading, setLoading] = useState(true)
  const [routeName, setRouteName] = useState<"LoginPin" | "SignIn">("SignIn")
  const { width, height } = useWindowDimensions()

  // Redux dispatch
  const dispatch = useAppDispatch()

  const checkScreenMode = async () => {
    await AsyncStorage.getItem("screenMode").then((screenMode: string | null) => {
      dispatch(setDeviceMode(screenMode ? screenMode : ""));
    })
  }

  async function loginStatusCheck() {
    /*
     * 15.9 / 15.10 - authentication is restored *before* the route is
     * evaluated, never after.
     *
     * On web a browser refresh throws away the Redux store, so isLoggedIn
     * is false on every start - including the ones where the session is
     * still good. Deciding the route from that unhydrated flag is the race
     * the spec describes: the auth stack mounts, React Navigation rewrites
     * the URL to the sign-in screen, and the session turns up a moment
     * later with nowhere to go.
     *
     * resolveInitialAuth resolves false immediately on Android
     * (authBoot.tsx is an empty stand-in there), so the storage check below
     * runs exactly as it does today and native startup is unchanged.
     */
    const restored = await resolveInitialAuth()

    if (!restored) {
      let token = await AsyncStorage?.getItem("LoginToken")
      let email = await AsyncStorage?.getItem("Email")

      setRouteName((token && email) ? "LoginPin" : "SignIn")
    }

    setLoading(false)
    BootSplash.hide({ fade: true })
  }

  useEffect(() => {
    if (!isLoggedIn) {
      setLoading(true)
      loginStatusCheck()
    }
  }, [isLoggedIn])

  useEffect(() => {
    checkScreenMode()
  }, [screenMode])

  useEffect(() => {
    if (width > height) {
      dispatch(setLandscape(true))
    } else {
      dispatch(setLandscape(false))
    }
  }, [width, height])

  // authInitializing: nothing is routed until the answer is known (15.22).
  if (loading) {
    return (
      <AuthSplash />
    )
  }

  const handleScan = async (barcode: string) => {
    dispatch(setBarcodeValue(barcode))
  };

  return (
    <>
      {/*
        On web the global listener is the primary scan path: desktop USB/Bluetooth
        scanners behave like keyboards, so BarcodeListener.web.tsx watches the
        document for a scan burst. Android keeps its existing behaviour (the
        listener stays off there; scanning goes through the in-screen scanner).
      */}
      <BarcodeListener onScan={handleScan} enabled={Platform.OS === 'web'} />
      <NavigationContainer linking={linking} documentTitle={documentTitle}>
        <SafeAreaProvider>
          {/* <SafeAreaInsetsContext.Consumer>
            {(insets) => <View style={{
              flex: 1,
              paddingTop: insets?.top ?? 0,
              paddingBottom: insets?.bottom ?? 0,
              backgroundColor: appColors.lightBackgroundColor
            }}> */}
          {
            isLoggedIn ?
              <DrawerStack /> :
              <AuthStack RouteName={routeName} />
          }

          {/* </View>}

          </SafeAreaInsetsContext.Consumer> */}
        </SafeAreaProvider>
      </NavigationContainer>
      <Toast config={ZenToastConfig} position='bottom' bottomOffset={30} />
    </>
  )
}


export default App;